import React, { useRef, useState } from "react";
import { Camera, X, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { compressImage } from "@/lib/imageCompress";

/**
 * Drop-in logo display + uploader for Schools and Branches.
 *
 * Props:
 *  - kind: "schools" | "branches"
 *  - id:   doc id
 *  - logo: current base64 (or undefined)
 *  - size: "sm" | "md" | "lg"   (visual size of the avatar)
 *  - editable: bool — show upload/remove controls
 *  - onUpdated(newLogoOrNull): called after successful PATCH
 */
export default function LogoUploader({
  kind, id, logo, size = "md", editable = true, onUpdated,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const dims = size === "sm" ? "w-12 h-12" : size === "lg" ? "w-20 h-20" : "w-14 h-14";
  const iconSize = size === "sm" ? "w-5 h-5" : size === "lg" ? "w-9 h-9" : "w-6 h-6";

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file (PNG, JPG, etc.)");
      return;
    }
    setBusy(true);
    try {
      // Logos are typically small (avatars) — keep them under 400KB and 600px max.
      const compressed = await compressImage(file, { maxKB: 400, maxDim: 600 });
      const { data } = await api.patch(`/${kind}/${id}`, { logo_base64: compressed });
      toast.success("Logo updated");
      onUpdated?.(data.logo_base64 ?? compressed);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.patch(`/${kind}/${id}`, { logo_base64: "" });
      toast.success("Logo removed");
      onUpdated?.(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="relative inline-block"
      onClick={(e) => e.stopPropagation()}
      data-testid={`logo-uploader-${kind}-${id}`}
    >
      <div className={`${dims} rounded-2xl overflow-hidden bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] flex items-center justify-center shrink-0 ring-1 ring-[#2563EB]/15`}>
        {logo ? (
          <img src={logo} alt="logo" className="w-full h-full object-cover" data-testid={`logo-img-${kind}-${id}`} />
        ) : (
          <ImageIcon className={`${iconSize} text-[#2563EB]`} />
        )}
      </div>
      {editable && (
        <>
          <input
            ref={inputRef} type="file" accept="image/*" className="hidden"
            onChange={handleFile} data-testid={`logo-input-${kind}-${id}`}
          />
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white shadow-md flex items-center justify-center transition disabled:opacity-60"
            title="Upload logo"
            data-testid={`logo-upload-btn-${kind}-${id}`}
          >
            {busy ? <Upload className="w-3.5 h-3.5 animate-pulse" /> : <Camera className="w-3.5 h-3.5" />}
          </button>
          {logo && !busy && (
            <button
              type="button"
              onClick={remove}
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white border border-[#C62828]/30 text-[#C62828] hover:bg-[#FFEBEE] shadow-sm flex items-center justify-center transition"
              title="Remove logo"
              data-testid={`logo-remove-btn-${kind}-${id}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Static logo display (no upload controls) — used in admin lists.
 */
export function LogoAvatar({ logo, size = "md", fallbackClass = "bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] text-[#2563EB]" }) {
  const dims = size === "sm" ? "w-10 h-10" : size === "lg" ? "w-16 h-16" : "w-12 h-12";
  const iconSize = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-7 h-7" : "w-5 h-5";
  return (
    <div className={`${dims} rounded-2xl overflow-hidden flex items-center justify-center shrink-0 ${fallbackClass}`}>
      {logo
        ? <img src={logo} alt="logo" className="w-full h-full object-cover" />
        : <ImageIcon className={iconSize} />}
    </div>
  );
}
